import {
    bannerLandSpchBblBgDimensions,
    bannerLandTopPnlBgDimensions,
    bannerLandWideBgDimensions,
    bannerLandWithBtnBgDimensions,
    bannerMoonBgDimensions,
    bannerPortLrgBgDimensions,
    bannerRectMedBgDimensions,
    Dimensions as ExpectedDimensions,
    iaeCardDimensions,
    iaeDetailsPageDimensions,
    iapImageDimensions,
    mapDimensions,
    packScreenshotDimensions,
    packTabThumbnailDimensions,
    packThumbnailDimensions,
    packWideThumbnailDimensions,
    tabBackgroundDimensions,
    tabIconDimensions,
    tagIconDimensions,
} from '../settings';

import { MediaType } from '../types/enums/mediatype.enum';

type InputDimensions = { w: number; h: number };

export const mediaTypeToeExpectedDimensions: Partial<
    Record<MediaType, ExpectedDimensions | ExpectedDimensions[]>
> = {
    [MediaType.SCREENSHOT]: packScreenshotDimensions,
    [MediaType.THUMBNAIL]: packThumbnailDimensions,
    [MediaType.WIDE_THUMBNAIL]: packWideThumbnailDimensions,
    [MediaType.TAB_THUMBNAIL]: packTabThumbnailDimensions,
    [MediaType.TAB_BACKGROUND]: tabBackgroundDimensions,
    [MediaType.TAB_ICON]: tabIconDimensions,
    [MediaType.TAG_ICON]: tagIconDimensions,
    [MediaType.IAP_IMAGE]: iapImageDimensions,
    [MediaType.MAP_BACKGROUND]: mapDimensions,
    [MediaType.IAE_CARD]: iaeCardDimensions,
    [MediaType.IAE_DETAILS_PAGE]: iaeDetailsPageDimensions,
    [MediaType.BANNER_MOON_BACKGROUND]: bannerMoonBgDimensions,
    [MediaType.BANNER_LANDSCAPE_WITH_BUTTON_BACKGROUND]:
        bannerLandWithBtnBgDimensions,
    [MediaType.BANNER_LANDSCAPE_SPEECH_BUBBLE_BACKGROUND]:
        bannerLandSpchBblBgDimensions,
    [MediaType.BANNER_LANDSCAPE_TOP_PANEL_BACKGROUND]:
        bannerLandTopPnlBgDimensions,
    [MediaType.BANNER_PORTRAIT_LARGE_BACKGROUND]: bannerPortLrgBgDimensions,
    [MediaType.BANNER_LANDSCAPE_WIDE_BACKGROUND]: bannerLandWideBgDimensions,
    [MediaType.BANNER_RECTANGULAR_MEDIUM_BACKGROUND]: bannerRectMedBgDimensions,
};

export function validateDimensions(
    dimension: InputDimensions,
    expectedDimensions: ExpectedDimensions | ExpectedDimensions[],
) {
    if (Array.isArray(expectedDimensions)) {
        for (const expectedDimension of expectedDimensions) {
            try {
                return validateDimensions(dimension, expectedDimension);
            } catch {
                continue;
            }
        }
        const expectedDimensionsDisplayText = expectedDimensions
            .map((dimension) => `${dimension.h}x${dimension.w}`)
            .join(', ');
        throw new Error(
            `The image must be in one of the following dimensions: ${expectedDimensionsDisplayText}`,
        );
    }
    if (expectedDimensions.w && expectedDimensions.w !== dimension.w) {
        throw new Error(`The width must be ${expectedDimensions.w}px`);
    }
    if (expectedDimensions.h && expectedDimensions.h !== dimension.h) {
        throw new Error(`The height must be ${expectedDimensions.h}px`);
    }
    if (expectedDimensions.minW && dimension.w < expectedDimensions.minW) {
        throw new Error(
            `The width must be at least ${expectedDimensions.minW}px`,
        );
    }
    if (expectedDimensions.maxW && dimension.w > expectedDimensions.maxW) {
        throw new Error(
            `The width must be less than or equal to ${expectedDimensions.maxW}px`,
        );
    }
    if (
        'ratio' in expectedDimensions &&
        expectedDimensions.ratio !== dimension.h / dimension.w
    ) {
        throw new Error(
            `The aspect ratio must be ${expectedDimensions.ratio?.toFixed(2)}`,
        );
    }
}

export async function validateMediaDimensions(
    url: string,
    mediaType: MediaType,
) {
    const expectedDimension = mediaTypeToeExpectedDimensions[mediaType];
    if (!expectedDimension) {
        return;
    }
    return await validateImageDimensions(url, expectedDimension);
}

export async function validateImageDimensions(
    url: string,
    expectedDimensions: ExpectedDimensions | ExpectedDimensions[],
) {
    const image = new Image();
    return new Promise<void>((res, rej) => {
        image.onload = () => {
            const imageSize = { w: image.width, h: image.height };
            try {
                validateDimensions(imageSize, expectedDimensions);
            } catch (err) {
                rej(err);
            }
            res();
        };
        image.onerror = rej;
        image.src = url;
    });
}
